import type { ReactNode } from 'react';
import { VisitorFeatureBoundary } from './visitor-feature-boundary';
import { TeamFeatureBoundary } from './team-feature-boundary';
import type { FeatureKey } from '@cdf/api-client-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { featureLabel, routeFeatureStatus } from '@/features/tenancy/feature-ui';
import { tenantPath } from '@/features/tenancy/urls';
import { useEntityFeatures } from '@/hooks/use-entity-features';

const historyPaths: Partial<Record<FeatureKey, string>> = {
  booking: '/my-bookings',
  workplace_planning: '/work-plan'
};

const disabledFeaturePolicies: Partial<Record<FeatureKey, { blocksHistory: boolean; description: string }>> = {
  visitors: {
    blocksHistory: true,
    description: 'Visitors is unavailable in this entity. An administrator can turn it back on; existing visits and reservations are retained.'
  },
  staffed_services: {
    blocksHistory: true,
    description: 'This feature is unavailable in this entity. An administrator can turn it back on without losing existing offerings or requests.'
  },
  teams_and_colleagues: {
    blocksHistory: true,
    description: 'Teams and colleagues is unavailable in this entity. Existing records are retained and become available when it is enabled again.'
  }
};

type FeatureRouteGateProps = { feature: FeatureKey; action: 'new' | 'history'; children: ReactNode };

export function FeatureRouteGate(props: FeatureRouteGateProps) {
  const content = <ResolvedFeatureRouteGate {...props} />;
  if (props.feature === 'teams_and_colleagues') return <TeamFeatureBoundary fallback={content}>{content}</TeamFeatureBoundary>;
  return props.feature === 'visitors'
    ? <VisitorFeatureBoundary fallback={content}>{content}</VisitorFeatureBoundary>
    : content;
}

function ResolvedFeatureRouteGate({ feature, action, children }: FeatureRouteGateProps) {
  const featureQuery = useEntityFeatures();
  const status = (feature === 'visitors' || feature === 'teams_and_colleagues') && featureQuery.tenantSlug &&
    (featureQuery.isFetching || featureQuery.isPlaceholderData) ? 'loading' : routeFeatureStatus(featureQuery, feature);
  if (status === 'loading') return <p className="text-muted-foreground px-6 py-8 text-sm">Checking feature availability…</p>;
  if (status === 'error') return <p role="alert" className="text-destructive px-6 py-8 text-sm">Feature availability could not be loaded. Try again shortly.</p>;
  if (status === 'enabled') return <>{children}</>;
  const label = featureLabel(feature);
  const homePath = featureQuery.tenantSlug ? tenantPath(featureQuery.tenantSlug, '/') : '/';
  const history = historyPaths[feature];
  const historyPath = history && featureQuery.tenantSlug
    ? tenantPath(featureQuery.tenantSlug, history) : history;
  const disabledPolicy = disabledFeaturePolicies[feature];
  if (action === 'history' && !disabledPolicy?.blocksHistory) {
    return <><div role="status" className="border-border bg-muted/40 mx-auto mb-4 max-w-7xl rounded-lg border px-4 py-3 text-sm">
      {label} is off. You can review and finish existing authorized work here; new work is unavailable.
    </div>{children}</>;
  }
  return <div className="mx-auto max-w-xl px-4 py-12"><Card><CardHeader><CardTitle>{label} is off</CardTitle><CardDescription>{disabledPolicy?.description ?? <>New {label.toLowerCase()} work is unavailable in this entity. Existing authorized records remain available for completion.</>}</CardDescription></CardHeader><CardContent className="flex flex-wrap gap-2"><Button asChild variant="outline"><Link to={homePath}>Home</Link></Button>{historyPath ? <Button asChild><Link to={historyPath}>View existing work</Link></Button> : null}</CardContent></Card></div>;
}
